'use client'

import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { z } from 'zod'
import Flag from 'react-flagkit'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { RouteParamType, RouteParamTranslationsParam } from '@/types/route-param'
import { KeyIcon, LanguagesIcon, PlusCircleIcon, SaveIcon, WaypointsIcon, XIcon } from 'lucide-react'
import { useMetaStore } from '@/store'
import { useScrollToError, useUnsavedChangesGuard } from '@/hooks'
import { setValidationErrors } from '@/lib/utils'
import { Separator } from '@/components/ui/separator'
import { FormModeEnum } from '@/enums'
import { UnsavedChangesGuardDialog } from '@/components/partials'

type PropsType = {
  message?: RouteParamType
  onSubmit: (data: any) => void
  onSubmitAndNew?: (data: any) => void
  onCancel: () => void
  isLoading?: boolean
  mode: FormModeEnum
}

export type RouteParamFormRef = {
  handleValidationErrors: (error: any) => void
  reset: () => void
}

type FormData = {
  key: string
  translations: RouteParamTranslationsParam
}

const RouteParamForm = forwardRef<RouteParamFormRef, PropsType>(
  ({ message, onSubmit, onSubmitAndNew, onCancel, isLoading = false, mode }, ref) => {
    const t = useTranslations()
    const { scrollToFirstError } = useScrollToError()

    const { metaState } = useMetaStore()
    const locales = useMemo(() => metaState.response?.locales || [], [metaState.response?.locales])
    const getCountryCodeFromLocale = (localeCode: string): string => {
      const parts = localeCode.split('-')
      return parts.length > 1 ? parts[1] : parts[0].toUpperCase()
    }
    const hasLocaleError = (localeCode: string): boolean => {
      return !!formErrors.translations?.[localeCode]?.value
    }

    const schema = useMemo(() => {
      const translationsSchema: Record<string, z.ZodObject<{ value: z.ZodString }>> = {}

      locales.forEach(locale => {
        translationsSchema[locale.fullCode] = z.object({
          value: z.string().min(1, t('not_blank'))
        })
      })

      return z.object({
        key: z.string().min(1, t('not_blank')),
        translations: z.object(translationsSchema)
      })
    }, [locales, t])

    const getDefaultValues = useCallback((): FormData => {
      const defaultTranslations: RouteParamTranslationsParam = {}

      locales.forEach(locale => {
        // Ensure values are always strings, never undefined
        const translation = message?.translations?.[locale.fullCode]
        defaultTranslations[locale.fullCode] = {
          value: translation?.value ?? ''
        }
      })

      return {
        key: message?.key ?? '',
        translations: defaultTranslations
      }
    }, [locales, message])

    type SchemaType = z.infer<typeof schema>

    const form = useForm<SchemaType>({
      resolver: zodResolver(schema),
      defaultValues: getDefaultValues() as SchemaType,
      mode: 'onChange'
    })
    const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(form.formState.isDirty)

    const formErrors = form.formState.errors

    useImperativeHandle(
      ref,
      () => ({
        handleValidationErrors: (error: any) => {
          setValidationErrors(error, form.setError)
          scrollToFirstError()
        },
        reset: () => form.reset()
      }),
      [form, scrollToFirstError]
    )

    const handleSubmit = (data: SchemaType) => {
      const submissionData = {
        ...data,
        translations: {} as RouteParamTranslationsParam
      }

      locales.forEach(locale => {
        submissionData.translations[locale.fullCode] = {
          value: data.translations[locale.fullCode]?.value || ''
        }
      })

      onSubmit(submissionData)
    }

    const handleSubmitAndNew = (data: SchemaType) => {
      const submissionData = {
        ...data,
        translations: {} as RouteParamTranslationsParam
      }

      locales.forEach(locale => {
        submissionData.translations[locale.fullCode] = {
          value: data.translations[locale.fullCode]?.value || ''
        }
      })

      onSubmitAndNew?.(submissionData)
    }

    const isCreateModeWithSubmitAndNew = mode === FormModeEnum.CREATE && !!onSubmitAndNew

    if (!metaState.response?.locales) {
      return (
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="space-y-4">
              <Skeleton className="h-6 w-32" />
              <div className="flex gap-2">
                <Skeleton className="h-10 w-24" />
                <Skeleton className="h-10 w-24" />
                <Skeleton className="h-10 w-24" />
              </div>
              <div className="space-y-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-20 w-full" />
              </div>
            </div>
          </CardContent>
        </Card>
      )
    }

    return (
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(
            isCreateModeWithSubmitAndNew ? handleSubmitAndNew : handleSubmit,
            scrollToFirstError
          )}
          className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <WaypointsIcon className="h-5 w-5" />
                {mode === FormModeEnum.CREATE ? t('create_route_param') : t('edit_route_param')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Key Field */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-1 lg:grid-cols-2">
                <FormField
                  control={form.control}
                  name="key"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required className="flex items-center gap-2">
                        <KeyIcon className="h-4 w-4" />
                        {t('key')}
                      </FormLabel>
                      <FormControl>
                        <Input placeholder={t('key')} {...field} autoFocus />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Translations */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <LanguagesIcon className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">{t('translations')}</h3>
                </div>

                {locales.length > 0 ? (
                  <Tabs
                    defaultValue={locales[0].fullCode}
                    className="w-full"
                    onValueChange={value => {
                      setTimeout(() => {
                        const input = document.querySelector(`[name="translations.${value}.value"]`) as HTMLInputElement
                        if (input) {
                          input.focus()
                        }
                      }, 100)
                    }}>
                    <TabsList className="min-h-10.5 w-full">
                      {locales.map(locale => {
                        const hasError = hasLocaleError(locale.fullCode)
                        return (
                          <TabsTrigger
                            key={locale.fullCode}
                            value={locale.fullCode}
                            className={`relative flex items-center gap-2 ${
                              hasError ? 'text-destructive border-destructive' : ''
                            }`}>
                            <Flag country={getCountryCodeFromLocale(locale.fullCode)} size={16} />
                            <span className="hidden sm:inline">{locale.name}</span>
                            {hasError && (
                              <div className="bg-destructive absolute -top-1 -right-1 h-2 w-2 animate-pulse rounded-full" />
                            )}
                          </TabsTrigger>
                        )
                      })}
                    </TabsList>

                    {locales.map(locale => {
                      const hasError = hasLocaleError(locale.fullCode)
                      return (
                        <TabsContent
                          key={locale.fullCode}
                          value={locale.fullCode}
                          forceMount
                          className="mt-6 space-y-4 data-[state=inactive]:hidden">
                          <div className="flex items-center gap-2">
                            <Flag country={getCountryCodeFromLocale(locale.fullCode)} size={20} />
                            <h4 className="text-lg font-semibold">{locale.name}</h4>
                            <Badge variant="outline" className="text-xs">
                              {locale.fullCode}
                            </Badge>
                            {hasError && (
                              <Badge variant="destructive" className="animate-pulse text-xs">
                                {t('error')}
                              </Badge>
                            )}
                          </div>

                          <FormField
                            control={form.control}
                            name={`translations.${locale.fullCode}.value`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel required>{t('value')}</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={t('value')}
                                    className={`${hasError ? 'border-destructive focus:border-destructive' : ''}`}
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                      )
                    })}
                  </Tabs>
                ) : (
                  <div className="py-8 text-center">
                    <p className="text-muted-foreground">{t('no_languages_available')}</p>
                  </div>
                )}
              </div>
              <Separator />
              <div className="flex items-center justify-between gap-3 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isLoading || form.formState.isSubmitting}>
                  <XIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('cancel')}</span>
                </Button>
                <div className="flex items-center gap-2">
                  {isCreateModeWithSubmitAndNew && (
                    <Button
                      type="button"
                      onClick={() => form.handleSubmit(handleSubmit, scrollToFirstError)()}
                      disabled={isLoading || form.formState.isSubmitting}>
                      <SaveIcon className="h-4 w-4" />
                      <span className="hidden sm:inline">
                        {isLoading || form.formState.isSubmitting ? t('saving') : t('save')}
                      </span>
                    </Button>
                  )}
                  <Button type="submit" disabled={isLoading || form.formState.isSubmitting}>
                    {isCreateModeWithSubmitAndNew ? (
                      <PlusCircleIcon className="h-4 w-4" />
                    ) : (
                      <SaveIcon className="h-4 w-4" />
                    )}
                    {isLoading || form.formState.isSubmitting
                      ? t('saving')
                      : isCreateModeWithSubmitAndNew
                        ? t('save_and_add_new')
                        : t(mode)}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          <UnsavedChangesGuardDialog
            open={isNavigationBlocked}
            onConfirm={confirmNavigation}
            onCancel={cancelNavigation}
          />
        </form>
      </Form>
    )
  }
)

RouteParamForm.displayName = 'RouteParamForm'

export default RouteParamForm
